'use client';
import { useEffect, createContext, useContext, useRef, useCallback } from 'react';

const useClickOutside = (ref, handler)=>{
    useEffect(()=>{
        const handleClickOutside = (event)=>{
            if (ref.current && !ref.current.contains(event.target)) {
                handler(event);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return ()=>{
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [
        ref,
        handler
    ]);
};

const DialogContext = createContext(null);
const useDialogContext = ()=>{
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error("useDialogContext must be used within a DialogRoot");
    }
    return context;
};
/**
 * Controller for the dialog element
 */ const useDialogControl = ({ open, setOpen, mode })=>{
    const ref = useRef(null);
    useEffect(()=>{
        const { current: dialog } = ref;
        if (mode === "popover") {
            open ? dialog.show() : dialog.close("no-sync");
            return;
        }
        if (open) {
            dialog.showModal();
            dialog.addEventListener("transitionstart", ()=>{
                dialog.setAttribute("data-state", "open");
            }, {
                once: true
            });
        } else {
            dialog.close("no-sync");
        }
    }, [
        open,
        mode
    ]);
    const handleClose = useCallback(()=>{
        if (mode === "popover") {
            setOpen(false);
        }
    }, [
        mode
    ]);
    useClickOutside(ref, handleClose);
    useEffect(()=>{
        const { current: dialog } = ref;
        const closeHandler = (e)=>{
            const target = e.target;
            if (target.returnValue !== "no-sync" || e.type === "cancel") {
                setOpen(false);
            }
            dialog.addEventListener("transitionend", ()=>{
                dialog.removeAttribute("data-state");
            }, {
                once: true
            });
        };
        const supportsTouch = "ontouchstart" in window;
        let clickedX = null;
        let clickedY = null;
        let clicked = false;
        // Define what "relatively close" means - adjust this value as needed
        const MOVEMENT_THRESHOLD = 10; // pixels
        // Track the initial click position
        const mDown = (e)=>{
            clicked = true;
            clickedX = e.clientX;
            clickedY = e.clientY;
        };
        const mMove = (e)=>{
            // Calculate distance between initial click and current position
            if (clicked && clickedX !== null && clickedY !== null) {
                const deltaX = Math.abs(e.clientX - clickedX);
                const deltaY = Math.abs(e.clientY - clickedY);
                // If movement exceeds threshold, consider it a drag rather than a click
                if (deltaX > MOVEMENT_THRESHOLD || deltaY > MOVEMENT_THRESHOLD) {
                    clicked = false;
                }
            }
        };
        // Add all event listeners
        const clickHandler = (e)=>{
            const { target: dialogTarget } = e;
            if (clicked || supportsTouch) {
                if (dialogTarget === dialog) {
                    dialog.close("dismiss");
                }
            }
        };
        dialog.addEventListener("close", closeHandler);
        dialog.addEventListener("cancel", closeHandler);
        dialog.addEventListener("click", clickHandler);
        document.addEventListener("mousedown", mDown);
        document.addEventListener("mousemove", mMove);
        return ()=>{
            dialog.removeEventListener("close", closeHandler);
            dialog.removeEventListener("cancel", closeHandler);
            dialog.removeEventListener("click", clickHandler);
            document.removeEventListener("mousedown", mDown);
            document.removeEventListener("mousemove", mMove);
        };
    }, [
        setOpen
    ]);
    return {
        open,
        setOpen,
        ref
    };
};

export { DialogContext as D, useDialogControl as a, useDialogContext as u };
